import { parseTermReviewEntries } from 'components/SearchNLP/filterReview';

/**
 * Maps each quoted parent term in the Lucene `term` string to the side it sits on.
 * Reuses the same NOT-detection as the top review chips (`parseTermReviewEntries`)
 * so include/exclude classification stays consistent across the modal.
 *
 * @param {string} term - the Lucene term string, e.g. `"cancer" AND NOT "neoplasm"`
 * @returns {Record<string, 'include' | 'exclude'>} keyed by lowercased phrase
 */
export function buildTermSideMap(term) {
  const map = {};
  for (const entry of parseTermReviewEntries(term)) {
    if (!entry || typeof entry.text !== 'string') continue;
    map[entry.text.toLowerCase()] = entry.excluded ? 'exclude' : 'include';
  }
  return map;
}

/** A term not present in the map defaults to inclusion. */
export function isExcludedTerm(sideMap, term) {
  return sideMap?.[String(term).toLowerCase()] === 'exclude';
}

/**
 * Predicate that accepts only parent terms on the same side as `term`. Used to
 * confine chip-toggle propagation (and the synonym→plural cascade) to one side,
 * so toggling an inclusion chip never flips its exclusion twin.
 */
export function sameSidePredicate(sideMap, term) {
  const excluded = isExcludedTerm(sideMap, term);
  return other => isExcludedTerm(sideMap, other) === excluded;
}

/**
 * Lowercased chip labels that fall on BOTH an inclusion and an exclusion parent.
 * Parent terms count as labels on their own side, so an inclusion term that also
 * appears as a related term of an exclusion parent (e.g. `cancer` under
 * `neoplasm`) is caught too. Consumers seed these OFF by default and keep them
 * out of the master "select all", so a shared term never silently lands on both
 * sides of the query.
 *
 * @param {Record<string, 'include' | 'exclude'>} sideMap
 * @param {...Record<string, string[]>} bucketDicts - e.g. termSynonyms, termPlurals
 * @returns {Set<string>}
 */
export function sharedAcrossSideLabels(sideMap, ...bucketDicts) {
  const include = new Set();
  const exclude = new Set();
  for (const buckets of bucketDicts) {
    for (const [term, labels] of Object.entries(buckets || {})) {
      const set = isExcludedTerm(sideMap, term) ? exclude : include;
      set.add(term.toLowerCase());
      for (const label of labels || []) {
        if (label) set.add(label.toLowerCase());
      }
    }
  }
  const shared = new Set();
  for (const label of include) {
    if (exclude.has(label)) shared.add(label);
  }
  return shared;
}
